/**
 * useWizard
 *
 * Owns the wizard flow state: current step index, navigation, and the
 * lifecycle flags (`cv_wizard_completed`) that drive the new-user redirect.
 *
 * IMPORTANT: We deliberately do NOT persist the current step to localStorage
 * anymore. The wizard always starts at 'welcome'. Only the "completed" flag
 * is persisted so the app knows whether to redirect first-time users.
 * CV data is persisted separately in useCvState.
 */

import { useCallback, useRef, useState } from 'react';

export type WizardStepKey =
  | 'welcome'
  | 'freetext'
  | 'import'
  | 'basics'
  | 'experience'
  | 'education'
  | 'skills'
  | 'more'
  | 'template'
  | 'prescoreats'
  | 'reformatage'
  | 'done';

/** Full wizard step order. 'freetext' and 'import' are mutually exclusive and optional. */
export const WIZARD_STEP_ORDER: WizardStepKey[] = [
  'welcome',
  'freetext',
  'import',
  'basics',
  'experience',
  'education',
  'skills',
  'more',
  'template',
  'prescoreats',
  'reformatage',
  'done',
];

export type WizardCompletedState = 'done' | 'skipped' | null;

const COMPLETED_KEY = 'cv_wizard_completed';

function readInitialCompleted(): WizardCompletedState {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(COMPLETED_KEY);
    if (stored === 'done' || stored === 'skipped') return stored;
  } catch {
    /* ignore */
  }
  return null;
}

export interface UseWizardReturn {
  step: WizardStepKey;
  stepIndex: number;
  totalSteps: number;
  completed: WizardCompletedState;
  showImportStep: boolean;
  setShowImportStep: (v: boolean) => void;
  goNext: () => void;
  goPrev: () => void;
  goTo: (step: WizardStepKey) => void;
  markDone: () => void;
  markSkipped: () => void;
  reset: () => void;
}

export function useWizard(): UseWizardReturn {
  // Always start at 'welcome' — no localStorage persistence for step
  const [step, setStep] = useState<WizardStepKey>('welcome');
  const [completed, setCompleted] = useState<WizardCompletedState>(readInitialCompleted);
  const [showImportStep, setShowImportStepState] = useState<boolean>(false);

  // Keep a ref that is updated SYNCHRONOUSLY so goNext/goPrev called right
  // after setShowImportStep() see the correct value (not the stale closure).
  const showImportStepRef = useRef<boolean>(false);

  const setShowImportStep = useCallback((v: boolean) => {
    showImportStepRef.current = v; // sync update — critical for Welcome → Import nav
    setShowImportStepState(v);
  }, []);

  const getVisible = () => {
    const showImport = showImportStepRef.current;
    return WIZARD_STEP_ORDER.filter((s) => {
      if (s === 'import') return showImport;
      if (s === 'freetext') return !showImport;
      return true;
    });
  };

  const goNext = useCallback(() => {
    setStep((current) => {
      const visible = getVisible();
      const idx = visible.indexOf(current);
      if (idx < 0 || idx >= visible.length - 1) return current;
      return visible[idx + 1] ?? current;
    });
  }, []);

  const goPrev = useCallback(() => {
    setStep((current) => {
      const visible = getVisible();
      const idx = visible.indexOf(current);
      if (idx <= 0) return current;
      return visible[idx - 1] ?? current;
    });
  }, []);

  const goTo = useCallback((target: WizardStepKey) => {
    if (WIZARD_STEP_ORDER.includes(target)) setStep(target);
  }, []);

  const markDone = useCallback(() => {
    setCompleted('done');
    try { window.localStorage.setItem(COMPLETED_KEY, 'done'); } catch { /* ignore */ }
    setStep('done');
  }, []);

  const markSkipped = useCallback(() => {
    setCompleted('skipped');
    try { window.localStorage.setItem(COMPLETED_KEY, 'skipped'); } catch { /* ignore */ }
  }, []);

  const reset = useCallback(() => {
    setStep('welcome');
    showImportStepRef.current = false;
    setShowImportStepState(false);
  }, []);

  const visibleSteps = WIZARD_STEP_ORDER.filter((s) => {
    if (s === 'import') return showImportStep;
    if (s === 'freetext') return !showImportStep;
    return true;
  });
  const currentIndex = Math.max(0, visibleSteps.indexOf(step));

  return {
    step,
    stepIndex: currentIndex,
    totalSteps: visibleSteps.length,
    completed,
    showImportStep,
    setShowImportStep,
    goNext,
    goPrev,
    goTo,
    markDone,
    markSkipped,
    reset,
  };
}
